/**
 * DL-62980 epic (D2 Playwright — Mapping Request Overview filter dropdown search across
 * paginated lists, DL-58981) — deterministic, non-faker fixture data for the three
 * paginated-search filter dropdowns on the `/purchase-orders` (Mapping Request Overview) page:
 * `Created by` / `Supplier contact` (org users), `Style supplier` (suppliers), and `Company`
 * (brand companies, supplier-side).
 *
 * Per the epic's central risk: these dropdowns only fetch once a search term is typed
 * (`ComboBox.tsx` / `Autocomplete.tsx` gate `enabled` on `parsedSearch.length >= 1`), so the only
 * way to prove search reaches the *whole* backing set (not a small hardcoded list a spec can
 * pass trivially against) is to seed a large volume of entries and place named, distinctive
 * targets among them — reachable only by searching a fragment unique to each target, never by
 * browsing. Deliberately NOT faker-based: faker is an app-wide singleton seeded once
 * (`styles.ts`'s `faker.seed(4242)`), so its output depends on the full cross-file call order and
 * is not safely predictable from an e2e spec running in a separate Node process. Every name here
 * is hand-authored so specs can assert on it directly.
 */

export type FilterVolumeUser = {
  id: number;
  name: string;
  email: string;
  organizationTypeId: number;
  isActive: boolean;
  avatarColor?: string;
};

/** OrganizationType.Designer = 1, OrganizationType.Supplier = 2 (`types/index.ts`). */
const DESIGNER_ORG_TYPE = 1;
const SUPPLIER_ORG_TYPE = 2;

const FILLER_COUNT = 180;

/** 180 filler org users, ids 6001-6180, deliberately generic/unfindable by any distinctive term. */
const fillerUsers: FilterVolumeUser[] = Array.from({ length: FILLER_COUNT }, (_, i) => ({
  id: 6001 + i,
  name: `Filler Org User ${String(i + 1).padStart(3, '0')}`,
  email: `filler.org.user.${i + 1}@example.com`,
  organizationTypeId: i % 2 === 0 ? DESIGNER_ORG_TYPE : SUPPLIER_ORG_TYPE,
  isActive: true,
  avatarColor: '176963',
}));

/**
 * DL-62981 (Testmo 1282194, spine) — `Created by` search target. Distinctive surname fragment
 * ("Quintrell") matches nothing else in the seed.
 */
export const CREATED_BY_TARGET: FilterVolumeUser = {
  id: 9001,
  name: 'Zophia Quintrell',
  email: 'zophia.quintrell@example.com',
  organizationTypeId: DESIGNER_ORG_TYPE,
  isActive: true,
  avatarColor: '176963',
};

/**
 * DL-62990 (Testmo 1282197) — hyphenated-surname search target, seeded beyond the first page
 * alongside the other targets.
 */
export const HYPHENATED_SURNAME_TARGET: FilterVolumeUser = {
  id: 9002,
  name: 'Baxter Ashworth-Kline',
  email: 'baxter.ashworth-kline@example.com',
  organizationTypeId: DESIGNER_ORG_TYPE,
  isActive: true,
  avatarColor: '176963',
};

/**
 * DL-62990 (Testmo 1282197) — email-local-part search target. The local part
 * ("marisol.oyelaran") is distinctive and does not appear in the display name, so a search for
 * it only succeeds if the API/dropdown matches against email too.
 */
export const EMAIL_LOCAL_PART_TARGET: FilterVolumeUser = {
  id: 9003,
  name: 'Marisol Oyelaran',
  email: 'marisol.oyelaran@example.com',
  organizationTypeId: DESIGNER_ORG_TYPE,
  isActive: true,
  avatarColor: '176963',
};

/**
 * DL-62999 (Testmo 1282207) — the "Supplier contact" end-to-end repro target (a supplier-type
 * user, distinct from `CREATED_BY_TARGET`, matching a PO's `supplierContactPerson`).
 */
export const SUPPLIER_CONTACT_TARGET: FilterVolumeUser = {
  id: 9004,
  name: 'Shweta Kohli',
  email: 'shweta.kohli@example.com',
  organizationTypeId: SUPPLIER_ORG_TYPE,
  isActive: true,
  avatarColor: '176963',
};

/** Interspersed near the middle of the filler block so none of them sit anywhere near index 0. */
export const FILTER_VOLUME_USERS: FilterVolumeUser[] = [
  ...fillerUsers.slice(0, 90),
  CREATED_BY_TARGET,
  HYPHENATED_SURNAME_TARGET,
  EMAIL_LOCAL_PART_TARGET,
  SUPPLIER_CONTACT_TARGET,
  ...fillerUsers.slice(90),
];

export type FilterVolumeSupplier = {
  id: number;
  name: string;
  isActive: boolean;
  isStyleSupplier: boolean;
  isItemSupplier: boolean;
};

const fillerSuppliers: FilterVolumeSupplier[] = Array.from({ length: FILLER_COUNT }, (_, i) => ({
  id: 8101 + i,
  name: `Filler Supplier ${String(i + 1).padStart(3, '0')}`,
  isActive: true,
  isStyleSupplier: true,
  isItemSupplier: true,
}));

/** DL-62982 (Testmo 1282195) — `Style supplier` search target, beyond the first page. */
export const STYLE_SUPPLIER_TARGET: FilterVolumeSupplier = {
  id: 8001,
  name: 'Beyondpage Textiles Ltd',
  isActive: true,
  isStyleSupplier: true,
  isItemSupplier: false,
};

/**
 * DL-62982 (Testmo 1282195) — shares the "Beyondpage" name fragment with the target above but is
 * NOT a style supplier, so it must never appear when searching that fragment on the `Style
 * supplier` filter (proves the handler honours `isStyleSupplier`, not just `search`).
 */
export const NON_STYLE_SUPPLIER_DECOY: FilterVolumeSupplier = {
  id: 8002,
  name: 'Beyondpage Facilities Group',
  isActive: true,
  isStyleSupplier: false,
  isItemSupplier: true,
};

export const FILTER_VOLUME_SUPPLIERS: FilterVolumeSupplier[] = [
  ...fillerSuppliers.slice(0, 90),
  STYLE_SUPPLIER_TARGET,
  NON_STYLE_SUPPLIER_DECOY,
  ...fillerSuppliers.slice(90),
];

export type FilterVolumeCompany = { id: number; name: string; isActive: boolean };

const fillerCompanies: FilterVolumeCompany[] = Array.from({ length: FILLER_COUNT }, (_, i) => ({
  id: 7101 + i,
  name: `Filler Company ${String(i + 1).padStart(3, '0')}`,
  isActive: true,
}));

/** DL-62983 (Testmo 1282198) — supplier-side `Company` filter search target. */
export const COMPANY_TARGET: FilterVolumeCompany = {
  id: 7001,
  name: 'Farhaven Apparel Co',
  isActive: true,
};

export const FILTER_VOLUME_COMPANIES: FilterVolumeCompany[] = [
  ...fillerCompanies.slice(0, 90),
  COMPANY_TARGET,
  ...fillerCompanies.slice(90),
];

/**
 * Fixed PO rows referencing the targets above, so selecting a target in a filter dropdown
 * resolves to a real, predictable row (ref number + status) rather than an arbitrary faker one.
 * Appended (not prepended/spliced in place of) the generated volume so existing PO-count/order
 * assertions in sibling specs are unaffected.
 */
export const FILTER_TARGET_PURCHASE_ORDERS = [
  {
    id: 90001,
    organizationId: 2,
    refNumber: 'PO-BEYOND-001',
    name: 'Beyond-page filter fixture — Created by',
    createdByUser: {
      id: CREATED_BY_TARGET.id,
      name: CREATED_BY_TARGET.name,
      email: CREATED_BY_TARGET.email,
      avatarColor: '#176963',
    },
    supplier: { id: 8301, name: 'Fixture Supplier A', isActive: true },
    company: { id: 7301, name: 'Fixture Company A', isActive: true },
    supplierContactPerson: {
      id: SUPPLIER_CONTACT_TARGET.id,
      name: SUPPLIER_CONTACT_TARGET.name,
      email: SUPPLIER_CONTACT_TARGET.email,
    },
    // `status.id` (below) is matched against the `Status` filter's OWN option ids, which come
    // from `/api/postates` (`genericData.ts` — the only handler registered for that exact URL;
    // re-verified against `mocks/api/index.ts` and a repo-wide search, so this is NOT a
    // registration race). `/api/postates`'s static 6-item list (Draft=1, Pending=2, Approved=3,
    // Rejected=4, Cancelled=5, Completed=6) is simply a different, narrower vocabulary than the
    // real per-row statuses (`purchaseOrderStatuses` in `purchaseOrdersHelpers.ts`, which is
    // served at the DIFFERENT route `/api/organizations/:orgId/purchase-order-states` and is not
    // what this filter's dropdown reads) — a genuine, pre-existing mock gap (found during
    // DL-62980, left unfixed/not further scoped as out of this epic's remit; flagged in the PR).
    // `id: 3` is chosen here because it is the real, currently-selectable 'Approved' option under
    // the default Pending tab — `name` stays 'Initiated' (independent of the filter dropdown's
    // own label) so this row keeps satisfying `filterByTabStatus`'s Pending-tab allow-list and
    // stays visible under the default tab.
    status: {
      id: 3,
      name: 'Initiated',
      localeCode: 'postate_initiated',
      visibilityStatus: 'Initiated_mapping',
      poMappingRequestTab: 'Pending',
    },
    createdAt: new Date('2026-05-01T00:00:00.000Z'),
    updatedAt: new Date('2026-05-01T00:00:00.000Z'),
    publishedAt: new Date('2026-05-01T00:00:00.000Z'),
    stylesCount: 3,
    itemsCount: 5,
    etd: new Date('2026-06-01T00:00:00.000Z'),
    approvedByUser: null,
    submitForApprovalByUser: {
      id: 1,
      name: 'Fixture Submitter',
      email: 'fixture.submitter@example.com',
      avatarColor: '176963',
    },
  },
  {
    id: 90002,
    organizationId: 2,
    refNumber: 'PO-BEYOND-002',
    name: 'Beyond-page filter fixture — Style supplier',
    createdByUser: {
      id: 6001,
      name: 'Filler Org User 001',
      email: 'filler.org.user.1@example.com',
      avatarColor: '#176963',
    },
    supplier: { id: STYLE_SUPPLIER_TARGET.id, name: STYLE_SUPPLIER_TARGET.name, isActive: true },
    company: { id: 7302, name: 'Fixture Company B', isActive: true },
    supplierContactPerson: null,
    // Same `status.id` as PO-BEYOND-001 (see that row's comment for the `/api/postates`
    // vocabulary gap) so both share the Status filter's real 'Approved' (id 3) option; `name`
    // stays 'Initiated' so this row keeps satisfying the Pending-tab allow-list.
    status: {
      id: 3,
      name: 'Initiated',
      localeCode: 'postate_initiated',
      visibilityStatus: 'Initiated_mapping',
      poMappingRequestTab: 'Pending',
    },
    createdAt: new Date('2026-05-02T00:00:00.000Z'),
    updatedAt: new Date('2026-05-02T00:00:00.000Z'),
    publishedAt: new Date('2026-05-02T00:00:00.000Z'),
    stylesCount: 2,
    itemsCount: 4,
    etd: new Date('2026-06-02T00:00:00.000Z'),
    approvedByUser: null,
    submitForApprovalByUser: {
      id: 1,
      name: 'Fixture Submitter',
      email: 'fixture.submitter@example.com',
      avatarColor: '176963',
    },
  },
  {
    id: 90003,
    organizationId: 2,
    refNumber: 'PO-BEYOND-003',
    name: 'Beyond-page filter fixture — Created by, second status',
    createdByUser: {
      id: CREATED_BY_TARGET.id,
      name: CREATED_BY_TARGET.name,
      email: CREATED_BY_TARGET.email,
      avatarColor: '#176963',
    },
    supplier: { id: 8303, name: 'Fixture Supplier C', isActive: true },
    company: { id: 7303, name: 'Fixture Company C', isActive: true },
    supplierContactPerson: null,
    // "On hold" (id 7 — not one of the Status filter's real, currently-selectable ids 1/3/4, see
    // PO-BEYOND-001's comment) so DL-62994's Status=Approved + Created by=Zophia Quintrell
    // intersection is a strict subset of the Created-by-alone set.
    status: {
      id: 7,
      name: 'On hold',
      localeCode: 'postate_on_hold',
      visibilityStatus: 'On Hold',
      poMappingRequestTab: 'Pending',
    },
    createdAt: new Date('2026-05-03T00:00:00.000Z'),
    updatedAt: new Date('2026-05-03T00:00:00.000Z'),
    publishedAt: new Date('2026-05-03T00:00:00.000Z'),
    stylesCount: 1,
    itemsCount: 2,
    etd: new Date('2026-06-03T00:00:00.000Z'),
    approvedByUser: null,
    submitForApprovalByUser: {
      id: 1,
      name: 'Fixture Submitter',
      email: 'fixture.submitter@example.com',
      avatarColor: '176963',
    },
  },
  {
    id: 90004,
    organizationId: 2,
    refNumber: 'PO-BEYOND-004',
    name: 'Beyond-page filter fixture — Company (supplier-side)',
    createdByUser: {
      id: 6002,
      name: 'Filler Org User 002',
      email: 'filler.org.user.2@example.com',
      avatarColor: '#176963',
    },
    supplier: { id: 8304, name: 'Fixture Supplier D', isActive: true },
    company: { id: COMPANY_TARGET.id, name: COMPANY_TARGET.name, isActive: true },
    supplierContactPerson: null,
    status: {
      id: 4,
      name: 'Initiated',
      localeCode: 'postate_initiated',
      visibilityStatus: 'Initiated_mapping',
      poMappingRequestTab: 'Pending',
    },
    createdAt: new Date('2026-05-04T00:00:00.000Z'),
    updatedAt: new Date('2026-05-04T00:00:00.000Z'),
    publishedAt: new Date('2026-05-04T00:00:00.000Z'),
    stylesCount: 4,
    itemsCount: 6,
    etd: new Date('2026-06-04T00:00:00.000Z'),
    approvedByUser: null,
    submitForApprovalByUser: {
      id: 1,
      name: 'Fixture Submitter',
      email: 'fixture.submitter@example.com',
      avatarColor: '176963',
    },
  },
  {
    // DL-62983 (Testmo 1282198) fix — the supplier-side `Company` filter test runs as the
    // supplier demo user ("Jane Supplier", `mocks/data/user.ts`), whose `loginContext.organizationId`
    // is 63, NOT 2. The purchase-orders list handler (`mocks/api/handlers/purchaseOrders.ts`) is
    // tenant-scoped by that requesting orgId (its `purchaseOrdersDataCache` is keyed by it, and
    // fixture rows are now filtered to match — see that handler), so a row with
    // `organizationId: 2` (like PO-BEYOND-004 above) never reaches an org-63 request. This row is
    // organizationId 63's own properly-scoped equivalent, so the Company-filter test proves real
    // per-org search behaviour instead of passing by accident on another org's leaked row.
    id: 90005,
    organizationId: 63,
    refNumber: 'PO-BEYOND-005',
    name: 'Beyond-page filter fixture — Company (supplier-side, org 63)',
    createdByUser: {
      id: 6003,
      name: 'Filler Org User 003',
      email: 'filler.org.user.3@example.com',
      avatarColor: '#176963',
    },
    supplier: { id: 8305, name: 'Fixture Supplier E', isActive: true },
    company: { id: COMPANY_TARGET.id, name: COMPANY_TARGET.name, isActive: true },
    supplierContactPerson: null,
    status: {
      id: 4,
      name: 'Initiated',
      localeCode: 'postate_initiated',
      visibilityStatus: 'Initiated_mapping',
      poMappingRequestTab: 'Pending',
    },
    createdAt: new Date('2026-05-05T00:00:00.000Z'),
    updatedAt: new Date('2026-05-05T00:00:00.000Z'),
    publishedAt: new Date('2026-05-05T00:00:00.000Z'),
    stylesCount: 4,
    itemsCount: 6,
    etd: new Date('2026-06-05T00:00:00.000Z'),
    approvedByUser: null,
    submitForApprovalByUser: {
      id: 1,
      name: 'Fixture Submitter',
      email: 'fixture.submitter@example.com',
      avatarColor: '176963',
    },
  },
];
